import React, {useCallback, useRef, useState} from 'react';
import {ModelDTO} from 'hemwb-api/dist/models/ModelDTO';
import {useMounted} from '../../../../../hooks/useMounted';
import {useDispatch} from 'react-redux';
import {ApiResponse} from 'hemwb-api';
import {Alert, Button, Checkbox, Typography} from 'antd';
import {DownloadOutlined} from '@ant-design/icons';
import {InformationClassificationIcon} from '../../../../icons/classification/InformationClassificationIcon';
import {tid} from '../../../../../testUtils';
import {downloadFromResponse} from '../../../../../utils/download';
import {modelDownloadSilent} from '../../../../../store/model';
import {trackModelDownloaded} from '../../../../../tracking/tracking';
import {defaultLegalNotice} from '../../../../core/defaultLegalNotice';

const {Text, Title} = Typography;

type ModelDownloadProps = {
  model: ModelDTO;
  onSuccess?: () => void;
  onCancel?: () => void;
};

const ModelDownload: React.FC<ModelDownloadProps> = ({model, onSuccess, onCancel}) => {
  const dispatch = useDispatch();
  const [downloading, setDownloading] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const mounted = useMounted();
  const cancelled = useRef(false);
  const downloadIfNotCancelled = useCallback(
    (response: ApiResponse<Blob>) => (cancelled.current ? Promise.reject() : downloadFromResponse(response.raw)),
    [],
  );

  const handleCancel = useCallback(() => {
    cancelled.current = true;
    onCancel?.();
  }, [onCancel]);

  const handleDownload = useCallback(() => {
    setDownloading(true);
    setDownloadError(null);
    cancelled.current = false;
    modelDownloadSilent(dispatch, model.uuid)
      .then(downloadIfNotCancelled)
      .then(() => {
        trackModelDownloaded(model);
        onSuccess?.();
      })
      .catch(async (err: unknown) => {
        if (!mounted.current || !err) {
          return;
        }
        let message = 'Download failed. Please try again.';
        try {
          const response = err as Response;
          if (response?.status === 404 || response?.status === 400) {
            message = 'No files are available for download for this model.';
          } else if (response?.status === 403) {
            message = 'You do not have permission to download this model.';
          }
        } catch {
          // use default message
        }
        setDownloadError(message);
      })
      .finally(() => {
        mounted.current && setDownloading(false);
      });
  }, [dispatch, downloadIfNotCancelled, model, mounted, onSuccess]);

  return (
    <div style={{display: 'flex', flexDirection: 'column', gap: '16px'}}>
      {/* Header */}
      <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
        <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
          <DownloadOutlined style={{fontSize: '22px', color: '#262626'}} />
          <Title level={4} style={{margin: 0, fontSize: '20px', fontWeight: 600}}>
            Download Model
          </Title>
        </div>
        <div style={{display: 'flex', alignItems: 'center', gap: '6px'}}>
          <InformationClassificationIcon
            classification={model.metadata.information_classification}
            style={{width: 120, height: 39}}
          />
        </div>
      </div>

      {/* Legal notice */}
      <Text style={{fontSize: '14px', color: '#262626', lineHeight: '1.6'}}>
        {model.metadata.legal_notice || defaultLegalNotice}
      </Text>

      {/* Checkbox */}
      <div style={{display: 'flex', alignItems: 'flex-start', gap: '8px'}}>
        <Checkbox
          {...tid('checkbox-confirm')}
          checked={confirmed}
          onChange={(e) => setConfirmed(e.target.checked)}
          style={{marginTop: '2px', flexShrink: 0}}
        />
        <span style={{cursor: 'pointer', fontSize: '14px'}} onClick={() => setConfirmed(!confirmed)}>
          I have read the legal notice
        </span>
      </div>

      {/* Error message */}
      {downloadError && <Alert type="error" showIcon message={downloadError} style={{marginTop: '4px'}} />}

      {/* Footer */}
      <div style={{display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '8px'}}>
        <Button onClick={handleCancel} disabled={downloading} {...tid('btn-cancel')}>
          Cancel
        </Button>
        <Button
          type="primary"
          icon={<DownloadOutlined />}
          onClick={handleDownload}
          loading={downloading}
          disabled={!confirmed}
          {...tid('btn-submit')}
        >
          Download
        </Button>
      </div>
    </div>
  );
};

export default ModelDownload;
